<script setup lang="ts">
// /second-opinion — mockup page 9 (M5). The revenue path.
//
// Split hero: the pitch on the left, the whole form on the right so it's
// visible without scrolling. Posts the common contact schema, so a lead
// from here carries everything the network already knows how to relay:
// structured address, lead intent, the pick-one intake answers, and the
// optional yes/no qualifiers.
//
// The mockup also carries a "What Homeowners Are Saying" testimonial row.
// It isn't here: those quotes were invented for the illustration, and
// invented testimonials are deceptive advertising. It goes in the day
// there are real ones with permission.
import { computed, reactive, ref } from 'vue'
import { NInput, NSelect } from 'naive-ui'
import { useMutation } from '@tanstack/vue-query'
import intakeContract from '@contracts/lead-intake.v1.json'
import qualifierContract from '@contracts/lead-qualifiers.v1.json'
import { postJson } from '@/shared/api/client'
import AddressAutocomplete from '@/shared/components/AddressAutocomplete.vue'
import LeadQualifiers from '@/shared/components/LeadQualifiers.vue'
import { leadIntent } from '@/shared/state/leadIntent'
import { contactContext, contactContextLabel } from '../store'
import type {
    AddressValue,
    ContactRequest,
    ContactResponse,
    IntakeField,
    LeadType,
    QualifierAnswers,
    QualifierDef,
} from '@/shared/api/types'

const FIELDS = intakeContract.fields as unknown as IntakeField[]
const QUALIFIERS = qualifierContract.qualifiers as unknown as QualifierDef[]

const field = (id: string) => FIELDS.find((f) => f.id === id)!
const optionsOf = (id: string) =>
    field(id).options.map((o) => ({ label: o.label, value: o.value }))

const form = reactive({
    name: '',
    phone: '',
    email: '',
    address: { formatted: '' } as AddressValue,
    postal_code: '',
    description: '',
})

const intake = reactive<Record<string, string | null>>({
    issue: null,
    system_age: null,
    repair_quote: null,
    timeframe: null,
    best_time: null,
})

const answers = ref<QualifierAnswers>({})
const detailsOpen = ref(false)
const submitted = ref(false)

const CONSIDERING: { value: LeadType | 'unsure'; label: string; icon: string }[] = [
    { value: 'repair', label: 'Repair', icon: '🔧' },
    { value: 'replace', label: 'Replace', icon: '❄️' },
    { value: 'unsure', label: 'Not Sure', icon: '❓' },
]
const considering = ref<LeadType | 'unsure' | null>(
    leadIntent.value ?? null,
)

function setConsidering(value: LeadType | 'unsure') {
    considering.value = value
    // "Not sure" is a real answer, not a repair lead — leave the intent
    // unstated so the backend classifies from the evidence instead.
    leadIntent.value = value === 'unsure' ? null : value
}

const askedQualifiers = computed(() =>
    QUALIFIERS.filter((q) => q.applies_to.includes(leadIntent.value ?? 'repair')),
)

const zipValid = computed(() => /^\d{5}$/.test(form.postal_code.trim()))

const valid = computed(
    () =>
        form.name.trim().length > 0 &&
        form.phone.trim().length > 0 &&
        form.email.trim().includes('@') &&
        zipValid.value &&
        !!intake.issue,
)

function onAnswer(id: string, value: boolean | null) {
    const next = { ...answers.value }
    if (value === null) delete next[id]
    else next[id] = value
    answers.value = next
}

const submit = useMutation({
    mutationFn: (body: ContactRequest) => postJson<ContactResponse>('/api/contact', body),
    onSuccess: () => {
        submitted.value = true
        window.scrollTo({ top: 0, behavior: 'smooth' })
    },
})

function onSubmit() {
    if (!valid.value) return
    submit.mutate({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.formatted.trim() || undefined,
        city: form.address.city,
        state: form.address.state,
        // A hand-typed ZIP is the one the visitor stands behind; only fall
        // back to the autocompleted one when they left the field empty.
        postal_code: form.postal_code.trim() || form.address.postal_code,
        place_id: form.address.place_id,
        description: form.description.trim() || undefined,
        lead_type: considering.value && considering.value !== 'unsure' ? considering.value : undefined,
        intake: Object.entries(intake)
            .filter(([, v]) => !!v)
            .map(([f, v]) => ({ field: f, value: v as string })),
        qualifiers: Object.entries(answers.value).map(([id, value]) => ({ id, value })),
        // A completed repair-or-replace assessment rides along when the
        // visitor came through the calculator; the backend replays it.
        context: contactContext.value,
        contact_ok: true,
    })
}

const BENEFITS = [
    {
        title: 'Save Money',
        body: 'We help you avoid unnecessary repairs and overpriced quotes.',
        icon: '<path d="M10.9 2v1.4c1.7.2 2.9 1.1 3.1 2.7h-2.2c-.1-.6-.6-1-1.5-1-1 0-1.6.4-1.6 1s.4.9 1.9 1.2c2.4.5 3.5 1.3 3.5 3.1 0 1.7-1.2 2.8-3.2 3V15H9.2v-1.5c-2-.2-3.3-1.3-3.4-3h2.2c.1.8.8 1.3 1.9 1.3 1.1 0 1.7-.4 1.7-1.1 0-.6-.4-.9-2-1.3-2.3-.5-3.3-1.3-3.3-3C6.3 4.8 7.4 3.7 9.2 3.5V2z"/>',
    },
    {
        title: 'Get Honest Answers',
        body: "Unbiased advice focused on what's best for your home and budget.",
        icon: '<path d="M10 1.6 3 4.6v5.2c0 4.3 3 8.3 7 9.2 4-.9 7-4.9 7-9.2V4.6z"/>',
    },
    {
        title: 'Connect With an HVAC Professional',
        body: 'We can connect you with an HVAC professional working with a licensed HVAC contractor in your area.',
        icon: '<path d="M10 1.8a5.2 5.2 0 0 0-5.2 5.2c0 3.9 5.2 11.2 5.2 11.2s5.2-7.3 5.2-11.2A5.2 5.2 0 0 0 10 1.8zm0 7.2a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>',
    },
]

const NEXT_STEPS = [
    { n: 1, title: 'You Submit', body: 'Fill out the quick form with details about your AC system and issue.' },
    { n: 2, title: 'We Connect You', body: 'We pass your request to an HVAC professional working with a licensed HVAC contractor in your area.' },
    { n: 3, title: 'They Reach Out', body: 'A local pro contacts you to learn more and schedule a free consultation.' },
    { n: 4, title: 'You Get Clear Answers', body: 'Honest advice, compared options, and the best decision for your home.' },
]
</script>

<template>
    <section v-if="submitted" class="done">
        <div class="done-card">
            <span class="tick" aria-hidden="true">✓</span>
            <h1>Request received.</h1>
            <p>
                An HVAC professional will reach out — usually within hours during business days.
                Nothing you shared is sold or used for anything else.
            </p>
        </div>
    </section>

    <template v-else>
        <section class="hero">
            <div class="wash" aria-hidden="true"></div>
            <div class="inner">
                <div class="pitch">
                    <h1>
                        Get a Free Second Opinion<br />
                        from an HVAC Professional
                    </h1>
                    <p class="free"><span class="hl">100% Free. No Obligation.</span></p>
                    <p class="lede">
                        Not sure what's wrong? Unsure if you should repair or replace? Already have
                        a quote and want to compare?
                    </p>

                    <ul class="benefits">
                        <li v-for="b in BENEFITS" :key="b.title">
                            <span class="chip">
                                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="b.icon"></svg>
                            </span>
                            <span class="text">
                                <strong>{{ b.title }}</strong>
                                <small>{{ b.body }}</small>
                            </span>
                        </li>
                    </ul>

                    <ul class="chips">
                        <li>✓ 100% Free</li>
                        <li>✓ No Obligation</li>
                        <li>✓ Your Information Is Private</li>
                    </ul>
                </div>

                <div class="form-card">
                    <h2>Tell Us About Your AC.<br />We'll Do the Rest.</h2>
                    <p class="secure">🔒 Your information is secure and never shared.</p>

                    <!-- Anyone arriving from an assessment has already answered
                         questions. Say what is travelling with the form rather
                         than attaching it silently — and so they know they
                         don't have to type it all again. -->
                    <p v-if="contactContextLabel" class="attached">
                        <strong>Attached:</strong> your “{{ contactContextLabel }}” answers
                        ({{ contactContext?.answers?.length ?? 0 }} of them). The pro sees those
                        alongside whatever you add below.
                    </p>

                    <div class="grid">
                        <label>
                            <span>Full Name *</span>
                            <NInput v-model:value="form.name" placeholder="John Smith" />
                        </label>
                        <label>
                            <span>Phone Number *</span>
                            <!-- No sample digits: the mockup's (904) 555-0187 is the
                                 placeholder number we're deliberately keeping off the site. -->
                            <NInput v-model:value="form.phone" inputmode="tel" placeholder="Your phone number" />
                        </label>
                        <label class="wide">
                            <span>Email Address *</span>
                            <NInput v-model:value="form.email" inputmode="email" placeholder="you@example.com" />
                        </label>
                        <label class="wide">
                            <span>Service Address</span>
                            <AddressAutocomplete
                                v-model="form.address"
                                placeholder="123 Your Street, St. Augustine, FL"
                            />
                        </label>
                        <label>
                            <span>ZIP Code *</span>
                            <NInput
                                v-model:value="form.postal_code"
                                inputmode="numeric"
                                :maxlength="5"
                                placeholder="32084"
                            />
                        </label>
                        <label>
                            <span>{{ field('best_time').question }}</span>
                            <NSelect
                                v-model:value="intake.best_time"
                                :options="optionsOf('best_time')"
                                placeholder="Select…"
                                clearable
                            />
                        </label>
                        <label class="wide">
                            <span>{{ field('issue').question }} *</span>
                            <NSelect
                                v-model:value="intake.issue"
                                :options="optionsOf('issue')"
                                placeholder="Select the main issue…"
                                clearable
                            />
                        </label>
                        <label>
                            <span>{{ field('system_age').question }}</span>
                            <NSelect
                                v-model:value="intake.system_age"
                                :options="optionsOf('system_age')"
                                placeholder="Select age range…"
                                clearable
                            />
                        </label>
                        <label>
                            <span>{{ field('repair_quote').question }}</span>
                            <NSelect
                                v-model:value="intake.repair_quote"
                                :options="optionsOf('repair_quote')"
                                placeholder="Select an option…"
                                clearable
                            />
                        </label>
                    </div>

                    <div class="considering">
                        <span class="label">What are you considering? *</span>
                        <div class="segments">
                            <button
                                v-for="c in CONSIDERING"
                                :key="c.value"
                                type="button"
                                class="segment"
                                :class="{ picked: considering === c.value }"
                                @click="setConsidering(c.value)"
                            >
                                <span aria-hidden="true">{{ c.icon }}</span> {{ c.label }}
                            </button>
                        </div>
                    </div>

                    <label class="block">
                        <span>{{ field('timeframe').question }}</span>
                        <NSelect
                            v-model:value="intake.timeframe"
                            :options="optionsOf('timeframe')"
                            placeholder="Select timeframe…"
                            clearable
                        />
                    </label>

                    <label class="block">
                        <span>Anything else we should know? <em>(Optional)</em></span>
                        <NInput
                            v-model:value="form.description"
                            type="textarea"
                            :autosize="{ minRows: 3, maxRows: 7 }"
                            :maxlength="2000"
                            placeholder="Tell us anything that will help us understand your situation…"
                        />
                    </label>

                    <button
                        class="details-toggle"
                        type="button"
                        @click="detailsOpen = !detailsOpen"
                    >
                        <span class="chev" :class="{ open: detailsOpen }">›</span>
                        A few optional details — helps the pro come prepared
                    </button>
                    <div v-show="detailsOpen" class="details-body">
                        <LeadQualifiers
                            :qualifiers="askedQualifiers"
                            :answers="answers"
                            @answer="onAnswer"
                        />
                    </div>

                    <p class="privacy">
                        🔒 We respect your privacy. Your information is never sold or shared beyond
                        the HVAC professional who contacts you.
                    </p>

                    <button class="submit" type="button" :disabled="!valid || submit.isPending.value" @click="onSubmit">
                        {{ submit.isPending.value ? 'Sending…' : 'Get My Free Second Opinion →' }}
                    </button>
                    <p class="fine">It only takes about 60 seconds.</p>
                    <p v-if="submit.isError.value" class="error">
                        {{ (submit.error.value as Error)?.message ?? 'Something went wrong — try again.' }}
                    </p>
                </div>
            </div>
        </section>

        <section class="next">
            <div class="next-inner">
                <h2>What Happens Next?</h2>
                <ol>
                    <li v-for="step in NEXT_STEPS" :key="step.n">
                        <span class="n">{{ step.n }}</span>
                        <strong>{{ step.title }}</strong>
                        <span class="body">{{ step.body }}</span>
                    </li>
                </ol>
            </div>
        </section>
    </template>
</template>

<style scoped lang="scss">
/* ---- hero + form ---- */
.hero {
    position: relative;
    background: var(--site-gradient-navy);
    color: #fff;
}

.wash {
    position: absolute;
    inset: 0;
    background: radial-gradient(90% 70% at 20% 0%, rgba(29, 95, 191, 0.45) 0%, rgba(7, 28, 69, 0) 60%);
}

.inner {
    position: relative;
    z-index: 1;
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-xl) var(--site-space-lg) var(--site-space-2xl);
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 980px) {
        grid-template-columns: 1fr;
    }
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.6rem, 3.1vw, 2.35rem);
    font-weight: 800;
    line-height: 1.18;
    margin: 0 0 0.4rem;
}

.free {
    margin: 0 0 var(--site-space-md);
    font-family: var(--site-font-heading);
    font-size: 1.25rem;
    font-weight: 800;
}

.lede {
    margin: 0 0 var(--site-space-lg);
    color: rgba(255, 255, 255, 0.85);
    max-width: 32rem;
}

.benefits {
    list-style: none;
    margin: 0 0 var(--site-space-lg);
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--site-space-md);

    li {
        display: flex;
        gap: 0.75rem;
        align-items: flex-start;
    }

    .text {
        display: flex;
        flex-direction: column;
        line-height: 1.35;
    }

    strong {
        font-size: 0.95rem;
    }

    small {
        font-size: 0.82rem;
        color: rgba(255, 255, 255, 0.75);
    }
}

.chip {
    display: grid;
    place-items: center;
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    flex-shrink: 0;

    svg {
        width: 1.25rem;
        height: 1.25rem;
        color: #ffb489;
    }
}

.chips {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--site-space-md);
    font-size: 0.82rem;
    font-weight: 600;
    color: #9ec5f8;
}

.form-card {
    background: #fff;
    border-radius: var(--site-radius-md);
    box-shadow: var(--site-shadow-lg);
    padding: var(--site-space-lg);
    color: var(--site-text);

    h2 {
        font-family: var(--site-font-heading);
        font-size: 1.3rem;
        font-weight: 800;
        color: var(--site-navy);
        margin: 0 0 0.35rem;
        line-height: 1.25;
    }
}

.secure {
    margin: 0 0 var(--site-space-md);
    font-size: 0.75rem;
    color: var(--site-text-muted);
}

.attached {
    margin: 0 0 var(--site-space-md);
    padding: 0.55rem 0.75rem;
    border-left: 3px solid var(--site-blue);
    border-radius: var(--site-radius-sm);
    background: var(--site-blue-soft);
    font-size: 0.8rem;
    line-height: 1.5;
    color: var(--site-text);

    strong {
        color: var(--site-navy);
    }
}

.grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--site-space-md);

    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
}

label,
.block {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;

    > span {
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--site-navy);

        em {
            font-style: normal;
            font-weight: 400;
            color: var(--site-text-muted);
        }
    }
}

.wide {
    grid-column: 1 / -1;
}

.block {
    margin-top: var(--site-space-md);
}

.considering {
    margin-top: var(--site-space-md);

    .label {
        display: block;
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--site-navy);
        margin-bottom: 0.35rem;
    }
}

.segments {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.4rem;

    /* Three across gave 89px per option on a phone — enough to fit the
       label only by wrapping it, and narrow enough that the middle one is
       an easy mis-tap. This is a required field on the lead form, so it
       stacks rather than squeezes. */
    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
}

.segment {
    padding: 0.55rem 0.4rem;
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-sm);
    background: #fff;
    color: var(--site-navy);
    font-family: var(--site-font-body);
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: var(--site-blue);
    }

    &.picked {
        background: var(--site-blue);
        border-color: var(--site-blue);
        color: #fff;
    }

    @media (max-width: 860px) {
        min-height: 44px;
        text-align: left;
        padding: 0.6rem 0.8rem;
    }
}

.details-toggle {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    width: 100%;
    margin-top: var(--site-space-md);
    padding: 0.5rem 0;
    background: none;
    border: none;
    border-top: 1px solid var(--site-border);
    cursor: pointer;
    color: var(--site-text-muted);
    font-family: var(--site-font-body);
    font-size: 0.82rem;
    text-align: left;

    .chev {
        color: var(--site-accent);
        transition: transform 0.15s ease;

        &.open {
            transform: rotate(90deg);
        }
    }
}

.details-body {
    padding-bottom: var(--site-space-sm);
}

.privacy {
    margin: var(--site-space-md) 0;
    font-size: 0.72rem;
    color: var(--site-text-muted);
}

.submit {
    width: 100%;
    padding: 0.85rem 1rem;
    border: none;
    border-radius: var(--site-radius-pill);
    background: var(--site-gradient-cta);
    color: #fff;
    font-family: var(--site-font-heading);
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--site-accent-strong);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.fine {
    margin: 0.5rem 0 0;
    text-align: center;
    font-size: 0.74rem;
    color: var(--site-text-muted);
}

.error {
    margin: var(--site-space-sm) 0 0;
    color: var(--site-danger);
    font-size: 0.85rem;
}

/* ---- what happens next ---- */
.next {
    background: var(--site-bg);
}

.next-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);

    h2 {
        font-family: var(--site-font-heading);
        font-size: clamp(1.4rem, 2.6vw, 1.9rem);
        font-weight: 800;
        color: var(--site-navy);
        text-align: center;
        margin: 0 0 var(--site-space-xl);
    }

    ol {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: var(--site-space-lg);

        @media (max-width: 820px) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    li {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 0.3rem;
    }

    .n {
        display: grid;
        place-items: center;
        width: 2.4rem;
        height: 2.4rem;
        border-radius: 50%;
        background: var(--site-accent);
        color: #fff;
        font-family: var(--site-font-heading);
        font-weight: 700;
    }

    strong {
        color: var(--site-navy);
        font-size: 0.95rem;
    }

    .body {
        font-size: 0.8rem;
        line-height: 1.45;
        color: var(--site-text-muted);
    }
}

/* ---- confirmation ---- */
.done {
    background: var(--site-bg-alt);
}

.done-card {
    max-width: 34rem;
    margin: 0 auto;
    padding: clamp(3rem, 9vw, 6rem) var(--site-space-lg);
    text-align: center;

    h1 {
        color: var(--site-navy);
        font-size: clamp(1.6rem, 3vw, 2.2rem);
        margin: var(--site-space-md) 0;
    }

    p {
        color: var(--site-text-muted);
        line-height: 1.6;
    }
}

.tick {
    display: inline-grid;
    place-items: center;
    width: 3.4rem;
    height: 3.4rem;
    border-radius: 50%;
    background: var(--site-green);
    color: #fff;
    font-size: 1.8rem;
}
</style>
